import {type RecordOf, Record, List} from 'immutable';
import type {
  MouseClickIXType,
  MouseHoverIXType,
  MouseMoveIXType,
  ScrollIntoViewIXType,
  ScrollingInViewIXType,
  PageLoadIXType,
  MouseMoveInViewportIXType,
  PageScrolledIXType,
  PageIsScrollingIXType,
  DropdownIXType,
  NavbarIXType,
  SliderIXType,
  TabIXType,
} from '@packages/systems/ix2/types-core';

import {
  defaultMouseClickIX,
  defaultMouseHoverIX,
  defaultMouseMoveIX,
  defaultScrollIntoViewIX,
  defaultScrollingInViewIX,
  defaultPageLoadIX,
  defaultMouseMoveInViewportIX,
  defaultPageScrolledIX,
  defaultPageIsScrollingIX,
  defaultDropdownIX,
  defaultNavbarIX,
  defaultSliderIX,
  defaultTabIX,
} from '@packages/systems/ix2/designer/records/pojo';

import {EventRecords} from './trigger-events-immutable';

export type IX2MouseClickIXType = RecordOf<MouseClickIXType>;
export const MouseClickIXRecord: Record.Factory<MouseClickIXType> = Record(
  defaultMouseClickIX,
  'MouseClickIXRecord'
);
export type IX2MouseHoverIXType = RecordOf<MouseHoverIXType>;
export const MouseHoverIXRecord: Record.Factory<MouseHoverIXType> = Record(
  defaultMouseHoverIX,
  'MouseHoverIXRecord'
);
export type IX2MouseMoveIXType = RecordOf<MouseMoveIXType>;
export const MouseMoveIXRecord: Record.Factory<MouseMoveIXType> = Record(
  defaultMouseMoveIX,
  'MouseMoveIXRecord'
);
export type IX2ScrollIntoViewIXType = RecordOf<ScrollIntoViewIXType>;
export const ScrollIntoViewIXRecord: Record.Factory<ScrollIntoViewIXType> =
  Record(defaultScrollIntoViewIX, 'ScrollIntoViewIXRecord');
export type IX2ScrollingInViewIXType = RecordOf<ScrollingInViewIXType>;
export const ScrollingInViewIXRecord: Record.Factory<ScrollingInViewIXType> =
  Record(defaultScrollingInViewIX, 'ScrollingInViewIXRecord');
export type IX2PageLoadIXType = RecordOf<PageLoadIXType>;
export const PageLoadIXRecord: Record.Factory<PageLoadIXType> = Record(
  defaultPageLoadIX,
  'PageLoadIXRecord'
);
export type IX2MouseMoveInViewportIXType = RecordOf<MouseMoveInViewportIXType>;
export const MouseMoveInViewportIXRecord: Record.Factory<MouseMoveInViewportIXType> =
  Record(defaultMouseMoveInViewportIX, 'MouseMoveInViewportIXRecord');
export type IX2PageScrolledIXType = RecordOf<PageScrolledIXType>;
export const PageScrolledIXRecord: Record.Factory<PageScrolledIXType> = Record(
  defaultPageScrolledIX,
  'PageScrolledIXRecord'
);
export type IX2PageIsScrollingIXType = RecordOf<PageIsScrollingIXType>;
export const PageIsScrollingIXRecord: Record.Factory<PageIsScrollingIXType> =
  Record(defaultPageIsScrollingIX, 'PageIsScrollingIXRecord');
export type IX2NavbarIXType = RecordOf<NavbarIXType>;
export const NavbarIXRecord: Record.Factory<NavbarIXType> = Record(
  defaultNavbarIX,
  'NavbarIXRecord'
);
export type IX2DropdownIXType = RecordOf<DropdownIXType>;
export const DropdownIXRecord: Record.Factory<DropdownIXType> = Record(
  defaultDropdownIX,
  'DropdownIXRecord'
);
export type IX2SliderIXType = RecordOf<SliderIXType>;
export const SliderIXRecord: Record.Factory<SliderIXType> = Record(
  defaultSliderIX,
  'SliderIXRecord'
);
export type IX2TabIXType = RecordOf<TabIXType>;
export const TabIXRecord: Record.Factory<TabIXType> = Record(
  defaultTabIX,
  'TabIXRecord'
);

export const InteractionRecords = {
  [defaultMouseClickIX.interactionTypeId]: MouseClickIXRecord,
  [defaultMouseHoverIX.interactionTypeId]: MouseHoverIXRecord,
  [defaultMouseMoveIX.interactionTypeId]: MouseMoveIXRecord,
  [defaultScrollIntoViewIX.interactionTypeId]: ScrollIntoViewIXRecord,
  [defaultScrollingInViewIX.interactionTypeId]: ScrollingInViewIXRecord,
  [defaultPageLoadIX.interactionTypeId]: PageLoadIXRecord,
  [defaultMouseMoveInViewportIX.interactionTypeId]: MouseMoveInViewportIXRecord,
  [defaultPageScrolledIX.interactionTypeId]: PageScrolledIXRecord,
  [defaultPageIsScrollingIX.interactionTypeId]: PageIsScrollingIXRecord,
  [defaultNavbarIX.interactionTypeId]: NavbarIXRecord,
  [defaultSliderIX.interactionTypeId]: SliderIXRecord,
  [defaultDropdownIX.interactionTypeId]: DropdownIXRecord,
  [defaultTabIX.interactionTypeId]: TabIXRecord,
} as const;

export const InteractionToEventPairRecords = {
  [defaultMouseClickIX.interactionTypeId]: [
    EventRecords.MOUSE_CLICK,
    EventRecords.MOUSE_SECOND_CLICK,
  ],
  [defaultMouseHoverIX.interactionTypeId]: [
    EventRecords.MOUSE_OVER,
    EventRecords.MOUSE_OUT,
  ],
  [defaultMouseMoveIX.interactionTypeId]: [EventRecords.MOUSE_MOVE],
  [defaultScrollIntoViewIX.interactionTypeId]: [
    EventRecords.SCROLL_INTO_VIEW,
    EventRecords.SCROLL_OUT_OF_VIEW,
  ],
  [defaultScrollingInViewIX.interactionTypeId]: [
    EventRecords.SCROLLING_IN_VIEW,
  ],
  [defaultPageLoadIX.interactionTypeId]: [
    EventRecords.PAGE_START,
    EventRecords.PAGE_FINISH,
  ],
  [defaultMouseMoveInViewportIX.interactionTypeId]: [
    EventRecords.MOUSE_MOVE_IN_VIEWPORT,
  ],
  [defaultPageScrolledIX.interactionTypeId]: [
    EventRecords.PAGE_SCROLL_UP,
    EventRecords.PAGE_SCROLL_DOWN,
  ],
  [defaultPageIsScrollingIX.interactionTypeId]: [EventRecords.PAGE_SCROLL],
  [defaultDropdownIX.interactionTypeId]: [
    EventRecords.DROPDOWN_OPEN,
    EventRecords.DROPDOWN_CLOSE,
  ],
  [defaultNavbarIX.interactionTypeId]: [
    EventRecords.NAVBAR_OPEN,
    EventRecords.NAVBAR_CLOSE,
  ],
  [defaultSliderIX.interactionTypeId]: [
    EventRecords.SLIDER_ACTIVE,
    EventRecords.SLIDER_INACTIVE,
  ],
  [defaultTabIX.interactionTypeId]: [
    EventRecords.TAB_ACTIVE,
    EventRecords.TAB_INACTIVE,
  ],
} as const;

export type InteractionRecordType =
  | IX2MouseClickIXType
  | IX2MouseHoverIXType
  | IX2MouseMoveIXType
  | IX2ScrollIntoViewIXType
  | IX2ScrollingInViewIXType
  | IX2PageLoadIXType
  | IX2MouseMoveInViewportIXType
  | IX2PageScrolledIXType
  | IX2PageIsScrollingIXType
  | IX2DropdownIXType
  | IX2NavbarIXType
  | IX2SliderIXType
  | IX2TabIXType;

export type InteractionsRecordType = List<InteractionRecordType>;
